import { Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

import { BrowserDetectorIcon } from '@/components/BrowserDetector/BrowserDetector.styled';

import { useConfirmation, useIsAnyDrawerOpen } from '@/context';

import { useTranslate } from '@/hooks/useTranslate';

import {
  DocumentSection,
  DocumentStatus,
  DocumentType,
} from '@/types/document';
import { RequiredDocumentSection } from '@/types/document';
import { User } from '@/types/user';

const ELIGIBLE_STORAGE_KEY = 'poaSelfAttestationEligible';

const getStoredEligible = (userId: string): boolean | null => {
  try {
    const raw = localStorage.getItem(`${ELIGIBLE_STORAGE_KEY}:${userId}`);
    if (raw === null) return null;
    return JSON.parse(raw) as boolean;
  } catch {
    return null;
  }
};

const setStoredEligible = (userId: string, value: boolean) => {
  try {
    localStorage.setItem(
      `${ELIGIBLE_STORAGE_KEY}:${userId}`,
      JSON.stringify(value),
    );
  } catch {
    // localStorage might be unavailable (private mode, quota exceeded, etc.)
  }
};

export default function useEligibleChangedPopup(
  requiredDocumentSections: RequiredDocumentSection[],
  user: User | undefined,
  onOpenProofOfAddress: () => void,
) {
  const { t } = useTranslate();
  const { showConfirmation } = useConfirmation();
  const isAnyDrawerOpen = useIsAnyDrawerOpen();

  // Set when an eligible -> ineligible transition is detected while another
  // drawer is open. Consumed once every other drawer has closed (see the
  // effect below). A ref, not state, since it shouldn't itself trigger a
  // re-render - only isAnyDrawerOpen flipping should.
  const isPopupPendingRef = useRef(false);

  const proofOfAddressSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.ProofOfAddress,
  );

  const isRequiredSelfAttestationOnly = proofOfAddressSection?.docs.every(
    doc => doc === DocumentType.SelfAttestationForProofOfAddress,
  );

  const hasUploadedSelfAttestation =
    user?.documents.filter(
      doc => doc.docType === DocumentType.SelfAttestationForProofOfAddress,
    ) || [];

  const isEligible = Boolean(
    isRequiredSelfAttestationOnly && hasUploadedSelfAttestation?.length > 0,
  );

  const showEligibleChangedPopup = () =>
    showConfirmation({
      showHeader: false,
      drawerImage: <BrowserDetectorIcon />,
      content: <ModalContent />,
      primaryText: t('common.button.continue'),
      primaryAction: onOpenProofOfAddress,
    });

  useEffect(() => {
    if (!user) return;

    const userId = user.id.toString();

    const previousValue = getStoredEligible(userId);

    // first run: just store the baseline, don't compare yet
    if (previousValue === null) {
      setStoredEligible(userId, isEligible);
      return;
    }

    // only trigger when going from eligible -> not eligible
    if (previousValue === true && isEligible === false) {
      if (
        proofOfAddressSection?.status !== DocumentStatus.Approved &&
        proofOfAddressSection?.status !== DocumentStatus.InReview
      ) {
        // Don't show the popup on top of another open drawer - defer it
        // until the last other drawer closes (see the effect below).
        if (isAnyDrawerOpen) {
          isPopupPendingRef.current = true;
        } else {
          showEligibleChangedPopup();
        }
      }
    }

    setStoredEligible(userId, isEligible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEligible, user]);

  // Fire the deferred popup once all other drawers have closed.
  useEffect(() => {
    if (isAnyDrawerOpen || !isPopupPendingRef.current) return;

    isPopupPendingRef.current = false;
    showEligibleChangedPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnyDrawerOpen]);
}

const ModalContent = () => {
  const { t } = useTranslate();

  return (
    <Stack
      spacing={1}
      direction="column"
      alignItems="center"
      textAlign="center"
      mb={2}
    >
      <Typography mb={2} variant="h3">
        {t('additionalProofOfAddress.header')}
      </Typography>
      <Typography variant="body2">
        {t('additionalProofOfAddress.description')}
      </Typography>
    </Stack>
  );
};
